<template>
  <AllocationCardLayout
    icon="lock"
    :color="PALETTE.semanticOverrides.portfolioType['Self-managed']"
    :title="$t('production_strategy.self_managed_certificates')"
    :amount="allocation.amount"
    :unit="$t('common.mwh')"
    :subtitle="$t('production_strategy.allocation')"
    :ariaLabel="$t('production_strategy.self_managed_allocation_card')"
  >
    <template #badge>
      <span
        class="badge text-white px-3 py-2"
        :style="{
          backgroundColor:
            PALETTE.semanticOverrides.portfolioType['Self-managed'],
        }"
      >
        {{ allocationLabel }}
      </span>
    </template>
    <template #action>
      <EditButton v-if="canEdit" @click="openModal" />
    </template>
  </AllocationCardLayout>

  <!-- Modal -->
  <ModalComponent
    ref="modalRef"
    size="lg"
    :submit-button-text="$t('common.save')"
    :submit="handleSave"
    :submit-disabled="isLoading"
  >
    <template #title>{{
      $t("production_strategy.self_managed_settings")
    }}</template>
    <template #body>
      <SelfManagedInstructionsEditor
        v-if="editablePortfolio"
        v-model="editablePortfolio"
        :registryDevices="registryDevices"
      />
      <div v-else class="text-center py-4">
        <p>{{ $t("common.loading") }}...</p>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import EditButton from "@/components/common/EditButton.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { PALETTE } from "@/composables/chartPalette";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type {
  Company,
  PortfolioWithInstructions,
  RegistryDevice,
} from "@/types";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useProductionStrategyStore } from "../../useProductionStrategyStore";
import { normalizePercentage } from "../PPA/ppaPortfolioUtils";
import AllocationCardLayout from "../AllocationCardLayout.vue";
import { useCompanyMode } from "../useCompanyMode";
import SelfManagedInstructionsEditor from "./SelfManagedInstructionsEditor.vue";

const props = defineProps<{
  allocation: {
    amount: number;
    percentage: number;
  };
  companies: Company[];
  registryDevices: RegistryDevice[];
}>();

const emit = defineEmits<{
  updated: [];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const productionStore = useProductionStrategyStore();

// Multi-company mode detection
const { isSingleCompany, currentCompany, canEdit } = useCompanyMode(
  computed(() => props.companies)
);

const modalRef = useTemplateRef<any>("modalRef");
const isLoading = ref(false);
const editablePortfolio = ref<PortfolioWithInstructions | null>(null);

const selectedDeviceCount = computed(() => {
  if (!isSingleCompany.value) return 0;
  const registryDeviceIds =
    productionStore.defaultHold?.portfolio_instructions?.registry_device_ids ??
    [];
  return registryDeviceIds.length;
});

// In single-company mode, use the configured percentage from the store.
// In multi-company mode, use the consolidated percentage from props to avoid
// showing one company's configured value for the aggregated allocation.
const displayPercentage = computed(() => {
  const rawPercentage = isSingleCompany.value
    ? (productionStore.defaultHold?.portfolio_instructions
        ?.allocate_percentage ?? 0)
    : props.allocation.percentage;
  return normalizePercentage(rawPercentage) ?? 0;
});

const allocationLabel = computed(() => {
  if (selectedDeviceCount.value > 0) {
    return t("hold_strategy.targeted_allocation_badge", {
      percentage: displayPercentage.value,
      deviceCount: selectedDeviceCount.value,
    });
  }

  return `${displayPercentage.value}% ${t("production_strategy.allocation")}`;
});

const openModal = () => {
  if (!currentCompany.value?.id) return;

  // Initialize editable portfolio when opening modal
  const currentHold = productionStore.defaultHold;
  editablePortfolio.value = currentHold
    ? ({ ...currentHold } as PortfolioWithInstructions)
    : productionStore.createHoldPortfolio(currentCompany.value.id);
  modalRef.value.showModal = true;
};

const closeModal = () => {
  modalRef.value.showModal = false;
};

const handleSave = async () => {
  try {
    if (!currentCompany.value?.id || !editablePortfolio.value) {
      globalErrorHandler(new Error("Missing company or portfolio data"));
      return;
    }

    isLoading.value = true;

    const success = await productionStore.saveHoldPortfolio(
      currentCompany.value.id,
      editablePortfolio.value
    );

    if (success) {
      globalSuccessHandler(
        t("portfolio_instructions.update_success_title"),
        t("portfolio_instructions.update_success")
      );
      closeModal();
      emit("updated");
    }
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped lang="scss"></style>
